import { useEffect, useRef } from "react";
import { Link, useParams, useNavigate, useSearchParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Navigation } from "@/components/ui/navigation";
import { Button } from "@/components/ui/button";
import { ArrowLeft, ChevronRight, Home, BookOpen, Clock, Target } from "lucide-react";
import { fetchSubjectOutline } from "@/lib/api/subjects";
import { Card, CardContent } from "@/components/ui/card";
import { getUnitNotesByIndex } from "@/lib/notes-content";
import { useAnalytics } from "@/hooks/useAnalytics";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";

export default function UnitNotes() {
  const { slug, unitId } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { trackNotesViewed } = useAnalytics();
  
  // Get topic query param for focused expansion from personalized learning
  // Database uses "{subject}-topic-{unit}-{topic}" format, notes use "topic-{unit}-{topic}"
  const rawFocusedTopic = searchParams.get("topic");
  const focusedTopic = rawFocusedTopic
    ? rawFocusedTopic.replace(/^[a-z-]+-(?=topic-)/, "") // Remove subject prefix
    : null;

  const outlineQuery = useQuery({
    queryKey: ["subject-outline", slug],
    queryFn: () => fetchSubjectOutline(slug!),
    enabled: Boolean(slug),
  });

  // Track notes view once when data loads
  const trackedRef = useRef<string | null>(null);
  const unit = outlineQuery.data?.units?.find((u) => u.id === unitId) ?? null;
  
  useEffect(() => {
    if (!slug || !unitId || !unit || trackedRef.current === unitId) return;
    
    trackNotesViewed({
      subjectSlug: slug,
      unitId,
      unitName: unit.name,
    });
    
    trackedRef.current = unitId;
  }, [slug, unitId, unit, trackNotesViewed]);

  if (outlineQuery.isLoading) {
    return (
      <div className="min-h-screen bg-background">
        <Navigation />
        <main className="container mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="h-32 animate-pulse rounded-lg bg-muted" />
        </main>
      </div>
    );
  }

  if (outlineQuery.isError || !outlineQuery.data) {
    return (
      <div className="min-h-screen bg-background">
        <Navigation />
        <main className="container mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="text-center">Subject not found</div>
        </main>
      </div>
    );
  }

  const { subject, units } = outlineQuery.data;
  const unitFromQuery = units.find((u) => u.id === unitId);

  if (!unitFromQuery) {
    return (
      <div className="min-h-screen bg-background">
        <Navigation />
        <main className="container mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="text-center">Unit not found</div>
        </main>
      </div>
    );
  }

  // Get actual notes content using the unit index (e.g., 1 for Unit 1)
  const notesContent = getUnitNotesByIndex(slug!, unitFromQuery.index);

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-slate-50">
      <Navigation />
      <main className="container mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Breadcrumbs */}
        <nav className="flex items-center text-sm text-muted-foreground mb-6 overflow-x-auto whitespace-nowrap pb-2">
          <Link to="/" className="hover:text-foreground flex items-center">
            <Home className="h-4 w-4 mr-1" />
            Home
          </Link>
          <ChevronRight className="h-4 w-4 mx-2" />
          <Link to="/subjects" className="hover:text-foreground">
            Subjects
          </Link>
          <ChevronRight className="h-4 w-4 mx-2" />
          <Link to={`/subjects/${slug}`} className="hover:text-foreground">
            {subject.name}
          </Link>
          <ChevronRight className="h-4 w-4 mx-2" />
          <Link to={`/subjects/${slug}/notes`} className="hover:text-foreground">
            Notes
          </Link>
          <ChevronRight className="h-4 w-4 mx-2" />
          <span className="text-foreground font-medium">{unitFromQuery.name}</span>
        </nav>

        <div className="max-w-4xl mx-auto">
          {/* Header Section */}
          <div className="mb-8">
            <div className="bg-gradient-to-r from-primary/10 via-primary/5 to-transparent p-6 sm:p-8 rounded-2xl border border-primary/10">
              <div className="flex items-start gap-4">
                <div className="p-3 bg-primary/10 rounded-xl">
                  <BookOpen className="h-8 w-8 text-primary" />
                </div>
                <div className="flex-1">
                  <h1 className="text-2xl sm:text-3xl font-extrabold tracking-tight text-slate-900">
                    {notesContent?.unitName || unitFromQuery.name}
                  </h1>
                  <p className="text-muted-foreground mt-2">
                    {subject.name} • Comprehensive Review Notes
                  </p>
                  {notesContent && (
                    <div className="flex flex-wrap gap-4 mt-4">
                      {notesContent.examWeight && (
                        <div className="flex items-center gap-2 text-sm">
                          <Target className="h-4 w-4 text-amber-600" />
                          <span className="text-slate-600">
                            <strong className="text-amber-600">Exam Weight:</strong> {notesContent.examWeight}
                          </span>
                        </div>
                      )}
                      {notesContent.focus && (
                        <div className="flex items-center gap-2 text-sm">
                          <Clock className="h-4 w-4 text-blue-600" />
                          <span className="text-slate-600 max-w-xl">
                            <strong className="text-blue-600">Focus:</strong> {notesContent.focus}
                          </span>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Notes Content */}
          {notesContent ? (
            <Card className="border-none shadow-lg bg-white/80 backdrop-blur-sm">
              <CardContent className="p-6 sm:p-8">
                <Accordion 
                  type="multiple" 
                  className="space-y-4" 
                  defaultValue={
                    focusedTopic 
                      ? notesContent.sections.filter(s => s.id === focusedTopic).map(s => s.id)
                      : notesContent.sections.map(s => s.id)
                  }
                >
                  {notesContent.sections.map((section) => (
                    <AccordionItem 
                      key={section.id} 
                      value={section.id}
                      className="border border-slate-200 rounded-xl overflow-hidden bg-white shadow-sm"
                    >
                      <AccordionTrigger className="px-6 py-4 hover:bg-slate-50 hover:no-underline">
                        <span className="text-lg font-bold text-slate-800 text-left">
                          {section.title}
                        </span>
                      </AccordionTrigger>
                      <AccordionContent className="px-6 pb-6 pt-2">
                        {section.content}
                      </AccordionContent>
                    </AccordionItem>
                  ))}
                </Accordion>
              </CardContent>
            </Card>
          ) : (
            /* Fallback Mock Content for units without notes */
            <Card className="border-none shadow-sm bg-white/50 backdrop-blur-sm">
              <CardContent className="p-8 sm:p-10 prose prose-slate max-w-none">
                <div className="text-center py-12">
                  <BookOpen className="h-16 w-16 text-muted-foreground/30 mx-auto mb-4" />
                  <h2 className="text-xl font-semibold text-slate-700 mb-2">
                    Notes Coming Soon
                  </h2>
                  <p className="text-muted-foreground max-w-md mx-auto">
                    Comprehensive notes for this unit are currently being developed. 
                    Check back soon for detailed content covering all learning objectives.
                  </p>
                </div>
              </CardContent>
            </Card>
          )}
          
          {/* Action Buttons */}
          <div className="mt-8 flex flex-col sm:flex-row justify-between gap-4">
            <Button variant="outline" onClick={() => navigate(-1)} className="gap-2">
              <ArrowLeft className="h-4 w-4" /> Back to Notes
            </Button>
            <Button asChild className="gap-2">
              <Link to={`/practice/${slug}/setup?unit=${unitFromQuery.id}`}>
                Practice this Unit <ChevronRight className="h-4 w-4" />
              </Link>
            </Button>
          </div>
        </div>
      </main>
    </div>
  );
}
